import { api } from './client'

export type Provedor = 'OpenAi' | 'Anthropic' | 'Google'

export type ConsumoPorAssistente = {
  assistenteId: number
  nome: string
  chamadas: number
  tokens: number
  custo: number
  custoMedioPorConversa: number
}

export type Consumo = {
  desde: string
  total: number
  chamadas: number
  falhas: number

  /** Uso da reserva é sinal de que o primário está instável — por isso está no painel. */
  reservas: number
  porAssistente: ConsumoPorAssistente[]
}

export type Projecao = {
  consumoNoPeriodo: number
  diasDoPeriodo: number
  mediaDiaria: number
  saldoInformado: number | null

  /** Nulo quando não há saldo informado ou não houve consumo — sem ritmo não há projeção. */
  diasRestantes: number | null
  emAlerta: boolean

  /**
   * Quando o saldo foi informado (demanda 140).
   *
   * 🔴 É este campo que faz o número deixar de envelhecer: com ele, o back
   * desconta o consumido desde então e responde *quanto resta agora* em vez de
   * *quanto foi colocado um dia*.
   */
  informadoEm: string | null

  /** O gasto desde a declaração. Nulo quando o saldo veio do ambiente, que não tem data. */
  consumidoDesde: number | null

  /** `saldoInformado − consumidoDesde`, nunca negativo. É o número da pergunta. */
  saldoAtual: number | null
}

export type ProvedorDisponivel = {
  provedor: Provedor
  configurado: boolean

  /** De onde a chave vem: `portal` (banco, cifrada) ou `arquivo` (recuo). */
  origem: 'portal' | 'arquivo' | null

  /**
   * 🔴 Os quatro últimos caracteres — a chave inteira NUNCA volta pela API.
   * Segredo que a API devolve é segredo que vaza no log do navegador, no HAR
   * e no print de tela. Quatro caracteres bastam para conferir o que se colou.
   */
  ultimosQuatro: string | null
}

export type ResultadoDoTeste = {
  ok: boolean

  /** Diz o que fazer — serviço fora do ar não é chave errada (`SEC-11`). */
  mensagem: string
}

/**
 * Os **seis** campos, não dois.
 *
 * 🔴 Teto de custo preenchido pela metade é limite que não limita, e a conta da
 * OpenAI é do cliente. Foi por isso que meia tela aqui foi cortada da 036 — e é
 * por isso que a tela volta inteira na 037.
 */
export type PedidoDeModelo = {
  provedor: Provedor
  modelo: string

  /** A reserva é o que sustenta a degradação graciosa da 008. */
  provedorDeReserva: Provedor | null
  modeloDeReserva: string | null

  tetoPorConversa: number
  tetoPorDia: number
}

export type ModeloDefinido = {
  provedor: Provedor
  modelo: string

  /** Preenchido quando a reserva está no mesmo provedor do primário. */
  aviso: string | null
}

export const ia = {
  consumo: (dias = 30) => api.get<Consumo>(`/ia/consumo?dias=${dias}`),
  saldo: () => api.get<Projecao>('/ia/saldo'),

  /** Informa o saldo da conta do provedor. Devolve a projeção já recalculada. */
  informarSaldo: (saldo: number) => api.put<Projecao>('/ia/saldo', { saldo }),
  provedores: () => api.get<ProvedorDisponivel[]>('/ia/provedores'),

  /** Vazio significa "mantém a que está" — o back preserva. */
  gravarChave: (provedor: Provedor, chave: string) =>
    api.put<ProvedorDisponivel>(`/ia/chaves/${provedor}`, { chave }),

  apagarChave: (provedor: Provedor) => api.delete<void>(`/ia/chaves/${provedor}`),

  /** Testa a chave que está VALENDO — do cofre ou do arquivo —, não o rascunho digitado. */
  testarChave: (provedor: Provedor) =>
    api.post<ResultadoDoTeste>(`/ia/chaves/${provedor}/testar`, {}),

  /**
   * Provedor e modelo de um assistente.
   *
   * Voltou na 037 depois de ter sido cortado da 036 às 08h05 de 07/08: com seis
   * campos, meia tela seria pior que nenhuma. O servidor recusa provedor sem
   * chave — a tela desabilita, mas quem barra é ele (`AUT-02`).
   */
  definirModelo: (assistenteId: number, pedido: PedidoDeModelo) =>
    api.put<ModeloDefinido>(`/ia/assistentes/${assistenteId}/modelo`, pedido),
}
